import * as React from "react";
import classNames from "classnames";
import { withStyles } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import TextField from "@material-ui/core/TextField";
import Chip from "@material-ui/core/Chip";
import MenuItem from "@material-ui/core/MenuItem";
import CreatableSelect from "react-select";
import CancelIcon from "@material-ui/icons/Cancel";
import Paper from "@material-ui/core/Paper";
import { emphasize } from "@material-ui/core/styles/colorManipulator";

const ITEM_HEIGHT = 48;

const styles: any = (theme) => ({
  root: {
    flexGrow: 1,
  },
  input: {
    display: "flex",
    padding: 0,
  },
  valueContainer: {
    display: "flex",
    flexWrap: "wrap",
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
    paddingBottom: 2,
    maxHeight: 80,
    overflowY: "scroll",
  },
  chip: {
    margin: `${theme.spacing.unit / 2}px ${theme.spacing.unit / 4}px`,
  },
  chipFocused: {
    backgroundColor: emphasize(
      theme.palette.type === "light"
        ? theme.palette.grey[300]
        : theme.palette.grey[700],
      0.08
    ),
  },
  noOptionsMessage: {
    padding: `${theme.spacing.unit}px ${theme.spacing.unit * 2}px`,
  },
  singleValue: {
    fontSize: 16,
  },
  placeholder: {
    margin: "0 auto",
    left: 2,
    fontSize: 24,
    color: "rgba(0, 0, 0, 0.39)",
    paddingBottom: 2,
  },
  paper: {
    position: "absolute",
    zIndex: 1,
    marginTop: theme.spacing.unit,
    left: 0,
    right: 0,
  },
});

function NoOptionsMessage(props) {
  return (
    <Typography
      color="textSecondary"
      className={props.selectProps.classes.noOptionsMessage}
      {...props.innerProps}
    >
      {props.children}
    </Typography>
  );
}

function inputComponent({ inputRef, ...props }) {
  return <div ref={inputRef} {...props} />;
}

function Control(props) {
  return (
    <TextField
      fullWidth
      InputProps={{
        inputComponent,
        inputProps: {
          className: props.selectProps.classes.input,
          inputRef: props.innerRef,
          children: props.children,
          ...props.innerProps,
        },
      }}
      {...props.selectProps.textFieldProps}
    />
  );
}

function Option(props) {
  return (
    <MenuItem
      buttonRef={props.innerRef}
      selected={props.isFocused}
      component="div"
      style={{
        fontWeight: props.isSelected ? 500 : 400,
      }}
      {...props.innerProps}
    >
      {props.children}
    </MenuItem>
  );
}

function Placeholder(props) {
  return (
    <Typography
      color="textSecondary"
      className={props.selectProps.classes.placeholder}
      {...props.innerProps}
      style={{ position: "absolute", marginLeft: "24%" }}
    >
      {props.children}
    </Typography>
  );
}

function SingleValue(props) {
  return (
    <Typography
      className={props.selectProps.classes.singleValue}
      {...props.innerProps}
    >
      {props.children}
    </Typography>
  );
}

function ValueContainer(props) {
  return (
    <div className={props.selectProps.classes.valueContainer}>
      {props.children}
    </div>
  );
}

function MultiValue(props) {
  return (
    <Chip
      tabIndex={-1}
      label={props.children}
      className={classNames(props.selectProps.classes.chip, {
        [props.selectProps.classes.chipFocused]: props.isFocused,
      })}
      onDelete={props.removeProps.onClick}
      deleteIcon={<CancelIcon {...props.removeProps} />}
    />
  );
}

function Menu(props) {
  return (
    <Paper
      square
      className={props.selectProps.classes.paper}
      {...props.innerProps}
    >
      {props.children}
    </Paper>
  );
}

const createOption = (label: string) => ({
  label,
  value: label,
});

const components = {
  Control,
  Menu,
  MultiValue,
  NoOptionsMessage,
  Option,
  Placeholder,
  SingleValue,
  ValueContainer,
  DropdownIndicator: null,
};

type IProps = {
  classes: any;
  theme: any;
  placeholder: string;
  value: any;
  handleChange: any;
  onInputChange: any;
  handleInputChange: any;
  isSearchable: any;
  onBlur: any;
  isClearable: any;
  onChange: any;
  inputValue: any;
  handleKeyDown: any;
  onKeyDown: any;
  isMulti?: boolean;
};

class CreateSelect extends React.Component<IProps> {
  state: any = {
    inputValue: "",
    value: [],
  };

  handleChange = (value: any, actionMeta: any) => {
    this.setState({ value });
  };

  handleInputChange = (inputValue: string) => {
    this.setState({ inputValue });
  };

  handleKeyDown = (event) => {
    const { inputValue, value } = this.state;
    if (!inputValue) return;
    switch (event.key) {
      case "Enter":
      case "Tab":
        this.setState({
          inputValue: "",
          value: [...value, createOption(inputValue)],
        });
        event.preventDefault();
    }
  };
  render() {
    const {
      classes,
      theme,
      inputValue,
      value,
      placeholder,
      handleInputChange,
      handleKeyDown,
      handleChange,
      isMulti = false,
    } = this.props;

    const createStyles = {
      fontSize: 30,
      input: (base) => ({
        ...base,
        color: theme.palette.text.primary,
        "& input": {
          font: "inherit",
        },
      }),
      control: (base) => ({
        ...base,
        height: ITEM_HEIGHT,
        "min-height": ITEM_HEIGHT,
      }),
    };
    return (
      <CreatableSelect
        autoFocus
        classes={classes}
        styles={createStyles}
        textFieldProps={{
          InputLabelProps: {
            shrink: true,
          },
        }}
        components={components}
        inputValue={inputValue}
        isClearable
        isMulti={isMulti}
        menuIsOpen={false}
        onChange={(e) => handleChange(e)}
        onSelectResetsInput={false}
        onBlurResetsInput={false}
        onInputChange={handleInputChange}
        onKeyDown={(e) => handleKeyDown(e)}
        placeholder={placeholder}
        value={value}
        noOptionsMessage={() => ""}
        maxMenuHeight={240}
      />
    );
  }
}

export default withStyles(styles, { withTheme: true })(CreateSelect);



// WEBPACK FOOTER //
// ./src/common/components/CreatableSelect.tsx